import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { X, Crown, Star, Zap } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';

interface SubscriptionModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export const SubscriptionModal: React.FC<SubscriptionModalProps> = ({ isOpen, onClose }) => {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { user } = useAuth();

  // RevenueCat Web Purchase Links
  const purchaseLinks = {
    awakening: 'https://pay.rev.cat/hixpaomfsqiizlxx/',
    aligned: 'https://pay.rev.cat/wkakexlkecdwkvvb/',
    abundant: 'https://pay.rev.cat/pitkauzffskwzaoh/'
  };

  const handleSubscribe = async (tier: keyof typeof purchaseLinks) => {
    // If user is not logged in, redirect to registration
    if (!user) {
      onClose();
      navigate('/register?redirect=payment');
      return;
    }

    setIsLoading(true);
    setError(null);
    
    try {
      // Construct RevenueCat purchase link with user ID
      // RevenueCat requires the App User ID at the end of the URL when Redemption Links are disabled
      const baseUrl = purchaseLinks[tier];
      const userIdForRevenueCat = user.id; // Use Supabase user ID as the App User ID
      const purchaseUrl = `${baseUrl}${userIdForRevenueCat}`;
      
      console.log(`Redirecting to RevenueCat purchase URL: ${purchaseUrl}`);
      
      // Redirect to RevenueCat purchase link with user ID
      window.location.href = purchaseUrl;
    } catch (err) {
      setError('Failed to process subscription. Please try again.');
      console.error('Subscription error:', err);
    } finally {
      setIsLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-white/10 backdrop-blur-md border border-white/20 rounded-3xl p-8 max-w-md w-full relative">
        {/* Close button */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 text-white/70 hover:text-white transition-colors"
        >
          <X size={24} />
        </button>

        {/* Header */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-purple-500/20 rounded-full mb-4">
            <Crown className="w-8 h-8 text-purple-400" />
          </div>
          <h2 className="text-2xl font-bold text-white mb-2">Unlock Premium</h2>
          <p className="text-white/70">
            Enhance your manifestation journey with premium features
          </p>
        </div>

        {/* Features */}
        <div className="space-y-4 mb-8">
          <div className="flex items-center space-x-3">
            <div className="w-6 h-6 bg-purple-500/20 rounded-full flex items-center justify-center">
              <Star className="w-3 h-3 text-purple-400" />
            </div>
            <span className="text-white/90">Unlimited AI conversations</span>
          </div>
          <div className="flex items-center space-x-3">
            <div className="w-6 h-6 bg-purple-500/20 rounded-full flex items-center justify-center">
              <Zap className="w-3 h-3 text-purple-400" />
            </div>
            <span className="text-white/90">Advanced manifestation tracking</span>
          </div>
          <div className="flex items-center space-x-3">
            <div className="w-6 h-6 bg-purple-500/20 rounded-full flex items-center justify-center">
              <Crown className="w-3 h-3 text-purple-400" />
            </div>
            <span className="text-white/90">Priority support</span>
          </div>
        </div>

        {/* Subscription Options */}
        <div className="space-y-4 mb-6">
          <button
            onClick={() => handleSubscribe('awakening')}
            disabled={isLoading}
            className="w-full bg-gradient-to-r from-purple-600 to-purple-500 hover:from-purple-700 hover:to-purple-600 disabled:from-purple-500/50 disabled:to-purple-400/50 text-white font-semibold py-3 px-6 rounded-xl transition-all duration-200 transform hover:scale-[1.02] disabled:scale-100 disabled:cursor-not-allowed"
          >
            {isLoading ? 'Processing...' : 'Awakening Soul - $11.11/month'}
          </button>
          
          <button
            onClick={() => handleSubscribe('aligned')}
            disabled={isLoading}
            className="w-full bg-gradient-to-r from-pink-600 to-rose-500 hover:from-pink-700 hover:to-rose-600 disabled:from-pink-500/50 disabled:to-rose-400/50 text-white font-semibold py-3 px-6 rounded-xl transition-all duration-200 transform hover:scale-[1.02] disabled:scale-100 disabled:cursor-not-allowed"
          >
            {isLoading ? 'Processing...' : 'Aligned Manifestor - $22.22/month'}
          </button>
          
          <button
            onClick={() => handleSubscribe('abundant')}
            disabled={isLoading}
            className="w-full bg-gradient-to-r from-amber-600 to-yellow-500 hover:from-amber-700 hover:to-yellow-600 disabled:from-amber-500/50 disabled:to-yellow-400/50 text-white font-semibold py-3 px-6 rounded-xl transition-all duration-200 transform hover:scale-[1.02] disabled:scale-100 disabled:cursor-not-allowed"
          >
            {isLoading ? 'Processing...' : 'Abundant Creator - $88.88/month'}
          </button>
        </div>

        {/* Error message */}
        {error && (
          <div className="bg-red-500/20 border border-red-500/30 rounded-lg p-3 mb-4">
            <p className="text-red-300 text-sm">{error}</p>
          </div>
        )}

        <p className="text-white/50 text-xs text-center mt-4">
          Cancel anytime. Secure payment via RevenueCat.
        </p>
      </div>
    </div>
  );
};